<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else class="pg-panel">
            <div class="pg-panel-header">
              <span class="pg-logo"><v-icon size="26" color="#ffffff">mdi-drama-masks</v-icon></span>
              <span class="pg-panel-title">PAGOS</span>
            </div>

            <div class="pg-panel-body">
              <!-- Las tres vistas del mockup (imgs 23, 24 y 25) -->
              <nav class="pg-tabs">
                <button
                  v-for="t in visibleTabs"
                  :key="t.key"
                  type="button"
                  class="pg-tab"
                  :class="{ 'pg-tab-on': tab === t.key }"
                  @click="tab = t.key"
                >
                  {{ t.label }}
                  <span class="pg-tab-count">{{ groups[t.key].length }}</span>
                </button>
              </nav>

              <p class="pg-tab-hint">{{ activeTab.hint }}</p>

              <div class="pg-search">
                <v-icon size="18" color="#9a9a9a">mdi-magnify</v-icon>
                <input
                  v-model="search"
                  type="search"
                  class="pg-search-input"
                  placeholder="Escriba por compañía de teatro, cliente u obra de teatro"
                />
              </div>

              <div class="pg-table-scroll">
                <!-- PAGOS REALIZADOS: lo que el cliente ya pagó (reserva y/o saldo) -->
                <table v-if="tab === 'realizados'" class="pg-table">
                  <thead>
                    <tr>
                      <th>OBRA</th>
                      <th>FECHA FUNCIÓN</th>
                      <th>FECHA PAGO 20%</th>
                      <th>FECHA PAGO SALDO</th>
                      <th>CLIENTE</th>
                      <th>COMPAÑÍA DE TEATRO</th>
                      <th>VALOR</th>
                      <th class="pg-docs-head">DOCUMENTOS DE PAGO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in rows" :key="r.id">
                      <td class="pg-cell-name">{{ playTitle(r) }}</td>
                      <td>{{ formatDate(functionDate(r)) }}</td>
                      <td>{{ formatDate(r.depositPaidAt) }}</td>
                      <td>{{ formatDate(r.balancePaidAt) }}</td>
                      <td>{{ clientName(r) }}</td>
                      <td>{{ companyName(r) }}</td>
                      <td>{{ formatCLP(paidAmount(r)) }}</td>
                      <td class="pg-docs">
                        <div class="pg-docs-inner">
                          <component :is="docTag(r.depositProofUrl)" :class="docClass(r.depositProofUrl)" v-bind="docAttrs(r, 'deposit_proof', r.depositProofUrl)">BOUCHER 20%</component>
                          <component :is="docTag(r.balanceProofUrl)" :class="docClass(r.balanceProofUrl)" v-bind="docAttrs(r, 'balance_proof', r.balanceProofUrl)">BOUCHER SALDO</component>
                          <component :is="docTag(r.reservationInvoiceUrl)" :class="docClass(r.reservationInvoiceUrl)" v-bind="docAttrs(r, 'reservation_invoice', r.reservationInvoiceUrl)">FACT. 20%</component>
                          <component :is="docTag(r.balanceInvoiceUrl)" :class="docClass(r.balanceInvoiceUrl)" v-bind="docAttrs(r, 'balance_invoice', r.balanceInvoiceUrl)">FACT. SALDO</component>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="rows.length === 0">
                      <td colspan="8" class="pg-empty">{{ emptyMessage }}</td>
                    </tr>
                  </tbody>
                </table>

                <!-- PAGOS POR REALIZAR: el cliente todavía le debe a la compañía -->
                <table v-else-if="tab === 'pendientes'" class="pg-table">
                  <thead>
                    <tr>
                      <th>OBRA</th>
                      <th>FECHA FUNCIÓN</th>
                      <th>CLIENTE</th>
                      <th>COMPAÑÍA DE TEATRO</th>
                      <th>PAGO PENDIENTE</th>
                      <th>MONTO</th>
                      <th class="pg-docs-head">FACTURAS EMITIDAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in rows" :key="r.id">
                      <td class="pg-cell-name">{{ playTitle(r) }}</td>
                      <td>{{ formatDate(functionDate(r)) }}</td>
                      <td>{{ clientName(r) }}</td>
                      <td>{{ companyName(r) }}</td>
                      <td><span class="pg-pending-tag">{{ pendingLabel(r) }}</span></td>
                      <td>{{ formatCLP(pendingAmount(r)) }}</td>
                      <td class="pg-docs">
                        <div class="pg-docs-inner">
                          <component :is="docTag(r.reservationInvoiceUrl)" :class="docClass(r.reservationInvoiceUrl)" v-bind="docAttrs(r, 'reservation_invoice', r.reservationInvoiceUrl)">FACT. 20%</component>
                          <component :is="docTag(r.balanceInvoiceUrl)" :class="docClass(r.balanceInvoiceUrl)" v-bind="docAttrs(r, 'balance_invoice', r.balanceInvoiceUrl)">FACT. SALDO</component>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="rows.length === 0">
                      <td colspan="7" class="pg-empty">{{ emptyMessage }}</td>
                    </tr>
                  </tbody>
                </table>

                <!-- PAGOS A LA COMPAÑÍA: el tramo entre la plataforma y la compañía
                     (anticipo del 10% con el 20% pagado; saldo del 80% con el 80%
                     pagado). El cliente no lo ve. -->
                <table v-else class="pg-table">
                  <thead>
                    <tr>
                      <th>OBRA</th>
                      <th>FECHA FUNCIÓN</th>
                      <th>CLIENTE</th>
                      <th>COMPAÑÍA DE TEATRO</th>
                      <th>ANTICIPO 10%</th>
                      <th>SALDO 80%</th>
                      <th class="pg-docs-head">DOCUMENTOS CÍA. / PLATAFORMA</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in rows" :key="r.id">
                      <td class="pg-cell-name">{{ playTitle(r) }}</td>
                      <td>{{ formatDate(functionDate(r)) }}</td>
                      <td>{{ clientName(r) }}</td>
                      <td>{{ companyName(r) }}</td>
                      <td>
                        {{ formatCLP(r.companyAdvanceAmount) }}
                        <span class="pg-pending-tag">{{ trackLabel(r, 'companyAdvanceProofUrl', 'companyAdvanceInvoiceUrl', r.companyAdvancePaidAt) }}</span>
                      </td>
                      <td>
                        {{ formatCLP(r.companyBalanceAmount) }}
                        <span class="pg-pending-tag">{{ balanceOpen(r) ? trackLabel(r, 'companyPaymentProofUrl', 'companyInvoiceUrl', r.companyPaidAt) : 'ESPERA EL 80% DEL CLIENTE' }}</span>
                      </td>
                      <td class="pg-docs">
                        <div class="pg-docs-inner">
                          <component :is="docTag(r.companyAdvanceProofUrl)" :class="docClass(r.companyAdvanceProofUrl)" v-bind="docAttrs(r, 'company_advance_proof', r.companyAdvanceProofUrl)">BOUCHER 10%</component>
                          <component :is="docTag(r.companyAdvanceInvoiceUrl)" :class="docClass(r.companyAdvanceInvoiceUrl)" v-bind="docAttrs(r, 'company_advance_invoice', r.companyAdvanceInvoiceUrl)">FACT. 10%</component>
                          <component :is="docTag(r.companyPaymentProofUrl)" :class="docClass(r.companyPaymentProofUrl)" v-bind="docAttrs(r, 'company_payment_proof', r.companyPaymentProofUrl)">BOUCHER 80%</component>
                          <component :is="docTag(r.companyInvoiceUrl)" :class="docClass(r.companyInvoiceUrl)" v-bind="docAttrs(r, 'company_invoice', r.companyInvoiceUrl)">FACT. 80%</component>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="rows.length === 0">
                      <td colspan="7" class="pg-empty">{{ emptyMessage }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="pg-volver-row">
            <button type="button" class="pg-volver" @click="goBack">
              <v-icon size="18">mdi-undo</v-icon>
              VOLVER
            </button>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchMyReservations } from "@/services/teatroApi";
import { reached } from "./reservationStatus";

// Reservas que aún no llegan al tramo de dinero (o que se cayeron).
const EXCLUDED = ["pending", "pending_availability", "date_requested", "cancelled"];

// El cliente ya terminó de pagarle a la compañía: el saldo se cerró y con eso
// se abrió el tramo entre la compañía y la plataforma.
const BALANCE_PAID = [
  "balance_paid",
  "company_invoice_pending",
  "company_payment_pending",
  "company_paid",
];


const TABS = [
  {
    key: "realizados",
    label: "PAGOS REALIZADOS",
    hint: "Pagos que los clientes ya hicieron: la reserva (20%) y el saldo (80%).",
  },
  {
    key: "pendientes",
    label: "PAGOS POR REALIZAR",
    hint: "Clientes que todavía no le pagan a la compañía de teatro.",
  },
  {
    key: "recibidos",
    label: "PAGOS A LA COMPAÑÍA",
    hint: "Tramo plataforma → compañía: anticipo del 10% (con el 20% del cliente pagado) y saldo del 80% (con el 80% pagado), cada uno con su factura.",
  },
];

export default {
  name: "PaymentsList",
  data() {
    return {
      reservations: [],
      tab: "realizados",
      search: "",
      loading: true,
      error: "",
    };
  },
  computed: {
    // El tramo compañía -> plataforma no le concierne al cliente que contrata.
    isClient() {
      return this.$store?.state?.role === "client";
    },
    visibleTabs() {
      return this.isClient ? TABS.filter((t) => t.key !== "recibidos") : TABS;
    },
    activeTab() {
      return this.visibleTabs.find((t) => t.key === this.tab) || this.visibleTabs[0];
    },
    // Reservas con movimiento de dinero, ya filtradas por rol en el backend.
    active() {
      return this.reservations.filter(
        (r) => !EXCLUDED.includes(String(r.status || "").toLowerCase())
      );
    },
    // Las tres vistas del mockup. No son excluyentes entre sí a propósito:
    // con la reserva (20%) pagada y el saldo (80%) pendiente, la reserva sale
    // en "realizados" y en "por realizar" a la vez. "Recibidos" es el flujo
    // compañía -> plataforma.
    groups() {
      const realizados = [];
      const pendientes = [];
      const recibidos = [];
      for (const r of this.active) {
        const status = String(r.status || "").toLowerCase();
        const balancePaid = BALANCE_PAID.includes(status);
        if (balancePaid || r.depositProofUrl) realizados.push(r);
        if (!balancePaid) pendientes.push(r);
        // El tramo con la compañía se abre con el 20% del cliente pagado.
        if (reached(r, "deposit_paid")) recibidos.push(r);
      }
      return { realizados, pendientes, recibidos };
    },
    rows() {
      const term = this.search.trim().toLowerCase();
      const list = this.groups[this.tab] || [];
      if (!term) return list;
      return list.filter((r) =>
        [this.playTitle(r), this.clientName(r), this.companyName(r)]
          .join(" ")
          .toLowerCase()
          .includes(term)
      );
    },
    emptyMessage() {
      if (this.search) return "Ningún pago coincide con la búsqueda.";
      return `No hay ${this.activeTab.label.toLowerCase()}.`;
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        // fetchMyReservations ya viene filtrado por rol: admin ve todo, la
        // compañía solo las reservas de sus obras.
        this.reservations = (await fetchMyReservations()) || [];
      } catch (e) {
        this.error = "No se pudieron cargar los pagos.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    playTitle(r) {
      return (r.play && r.play.title) || "—";
    },
    companyName(r) {
      return (r.play && r.play.company && r.play.company.name) || "—";
    },
    // La empresa del cliente; si no la hay, el nombre del contacto.
    clientName(r) {
      return (
        (r.client && r.client.company && r.client.company.name) ||
        (r.client && r.client.fullName) ||
        r.name ||
        "—"
      );
    },
    functionDate(r) {
      return r.confirmedDate || r.displayDate;
    },
    // Qué le falta pagar al cliente: primero la reserva, después el saldo.
    pendingLabel(r) {
      return r.depositProofUrl ? "SALDO 80%" : "RESERVA 20%";
    },
    // Lo que el cliente lleva pagado: la reserva, o el total si ya pagó el saldo.
    paidAmount(r) {
      const status = String(r.status || "").toLowerCase();
      return BALANCE_PAID.includes(status) ? r.totalAmount : r.depositAmount;
    },
    pendingAmount(r) {
      return r.depositProofUrl ? r.balanceAmount : r.depositAmount;
    },
    balanceOpen(r) {
      return reached(r, "balance_paid");
    },
    // Estado de un hito del tramo con la compañía: transferido y facturado,
    // transferido (falta la factura) o pendiente de transferir.
    trackLabel(r, proofKey, invoiceKey, paidAt) {
      if (!r[proofKey]) return "TRANSFERENCIA PENDIENTE";
      const when = paidAt ? ` ${this.formatDate(paidAt)}` : "";
      return r[invoiceKey] ? `PAGADO${when} · FACTURADO` : `PAGADO${when} · FALTA FACTURA`;
    },
    // Un enlace al visor si hay documento; un span deshabilitado si no.
    docTag(url) {
      return url ? "a" : "span";
    },
    docClass(url) {
      return url ? "pg-doc-btn" : "pg-doc-btn pg-doc-btn-off";
    },
    docAttrs(reservation, type, url) {
      return url ? { href: `/documento/${reservation.id}/${type}/` } : {};
    },
    formatCLP(amount) {
      const value = Number(amount) || 0;
      return "$ " + value.toLocaleString("es-CL");
    },
    formatDate(iso) {
      if (!iso) return "—";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return y && m && d ? `${d}/${m}/${y}` : "—";
    },
    goBack() {
      if (window.history.length > 1) window.history.back();
      else window.location.href = "/";
    },
  },
};
</script>

<style scoped>
.pg-panel {
  max-width: 1180px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.pg-panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 74px;
  padding: 0 26px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
}

.pg-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #111;
  border: 2px solid rgba(255, 255, 255, 0.5);
  flex: 0 0 auto;
}

.pg-panel-title {
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.pg-panel-body {
  padding: 24px 28px 30px;
}

/* --- Pestañas ------------------------------------------------------------ */
.pg-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.pg-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.9px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.pg-tab:hover {
  border-color: rgba(255, 255, 255, 0.4);
}

.pg-tab-on {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  border-color: transparent;
  color: #fff;
}

.pg-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.35);
  font-size: 10px;
  font-weight: 800;
}

.pg-tab-hint {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  margin: 0 0 18px;
}

.pg-search {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 460px;
  margin-bottom: 22px;
  padding: 9px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
}

.pg-search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 12px;
  letter-spacing: 0.4px;
}

.pg-search-input::placeholder {
  color: rgba(255, 255, 255, 0.45);
}

.pg-table-scroll {
  overflow-x: auto;
}

.pg-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.pg-table th {
  text-align: left;
  padding: 8px 12px 16px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.pg-table td {
  padding: 14px 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
}

.pg-table tbody tr:first-child td {
  border-top: none;
}

.pg-cell-name {
  text-transform: uppercase;
}

.pg-docs-head {
  white-space: normal;
}

.pg-docs-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pg-doc-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 11px;
  border-radius: 6px;
  background: #0070b9;
  color: #fff;
  text-decoration: none;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  white-space: nowrap;
  transition: transform 0.15s ease;
}

.pg-doc-btn:hover {
  transform: translateY(-1px);
}

.pg-doc-btn-off {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.35);
  cursor: default;
}

.pg-doc-btn-off:hover {
  transform: none;
}

.pg-pending-tag {
  display: inline-block;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(240, 182, 75, 0.16);
  color: #f0b64b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.6px;
}

.pg-empty {
  text-align: center;
  padding: 30px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  white-space: normal;
}

.pg-volver-row {
  max-width: 1180px;
  margin: 22px auto 0;
}

.pg-volver {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.pg-volver:hover {
  opacity: 0.72;
}
</style>
